export type LayerProvenance = {
  /** Short label for the header badge. */
  badge: string;
  /** Longer attribution for the Sources / Data Source rows. */
  source: string;
  /** True when the data is served by Esri rather than the agency of record. */
  esriHosted: boolean;
};

const ARCGIS_PROVENANCE: LayerProvenance = {
  badge: 'ArcGIS',
  source: 'ArcGIS Online',
  esriHosted: true,
};

/**
 * Matched against the URL host, longest suffix first, so `apps.fs.usda.gov`
 * resolves to the Forest Service rather than the generic USDA entry.
 */
const PROVENANCE_BY_HOST_SUFFIX: [string, LayerProvenance][] = [
  [
    'apps.fs.usda.gov',
    { badge: 'USDA', source: 'USDA Forest Service', esriHosted: false },
  ],
  [
    'gis.apfo.usda.gov',
    { badge: 'USDA', source: 'USDA Farm Service Agency (APFO)', esriHosted: false },
  ],
  ['usda.gov', { badge: 'USDA', source: 'USDA', esriHosted: false }],
  [
    'mapservices.weather.noaa.gov',
    { badge: 'NOAA', source: 'NOAA National Weather Service', esriHosted: false },
  ],
  ['weather.gov', { badge: 'NOAA', source: 'NOAA National Weather Service', esriHosted: false }],
  ['noaa.gov', { badge: 'NOAA', source: 'NOAA', esriHosted: false }],
  [
    'hazards.fema.gov',
    { badge: 'FEMA', source: 'FEMA Flood Map Service Center', esriHosted: false },
  ],
  ['fema.gov', { badge: 'FEMA', source: 'FEMA', esriHosted: false }],
  ['usgs.gov', { badge: 'USGS', source: 'US Geological Survey', esriHosted: false }],
  ['epa.gov', { badge: 'EPA', source: 'US Environmental Protection Agency', esriHosted: false }],
  ['arcgis.com', ARCGIS_PROVENANCE],
];

/**
 * Where a layer's data actually comes from, based on the serving host.
 *
 * Everything external used to be labelled "ArcGIS", which was misleading once
 * agency-hosted feeds were seeded alongside the Living Atlas mirrors: a Forest
 * Service or FEMA service is not Esri-hosted. Unknown hosts stay on ArcGIS
 * because user-imported layers are portal items.
 */
export function resolveLayerProvenance(
  serviceUrl: string | null | undefined
): LayerProvenance {
  if (!serviceUrl) return ARCGIS_PROVENANCE;

  let host: string;
  try {
    host = new URL(serviceUrl).hostname.toLowerCase();
  } catch {
    return ARCGIS_PROVENANCE;
  }

  for (const [suffix, provenance] of PROVENANCE_BY_HOST_SUFFIX) {
    if (host === suffix || host.endsWith(`.${suffix}`)) {
      return provenance;
    }
  }

  return ARCGIS_PROVENANCE;
}
